export const LOGIN_PATH = '/admin/login'

/** 登录态存 localStorage，同源多标签共享；关标签后仍保持，直到过期或主动退出 */
const LOGIN_CACHE_KEYS = [
  'token',
  'userId',
  'userName',
  'userNick',
  'userRoles',
  'userInfo',
  'user',
  'currentUser',
]

function parseJwtPayload(token) {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

function getStorageItem(key) {
  return localStorage.getItem(key)
}

function setStorageItem(key, value) {
  if (value == null || value === '') {
    localStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, String(value))
}

/** 清理旧版 sessionStorage 标签页登录态 */
export function purgeSessionLoginCache() {
  LOGIN_CACHE_KEYS.forEach((key) => sessionStorage.removeItem(key))
}

/**
 * 启动时：若 localStorage 尚无 token，而当前标签 sessionStorage 有，则迁移一次。
 * 避免从 sessionStorage 方案升级后已登录标签立刻掉线。
 */
export function migrateSessionLoginCache() {
  if (localStorage.getItem('token')) {
    purgeSessionLoginCache()
    return
  }
  const sessionToken = sessionStorage.getItem('token')
  if (!sessionToken) return
  LOGIN_CACHE_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(key)
    if (value != null && value !== '') {
      localStorage.setItem(key, value)
    }
  })
  purgeSessionLoginCache()
}

/** @deprecated 使用 migrateSessionLoginCache / purgeSessionLoginCache */
export function purgeLegacyLoginCache() {
  purgeSessionLoginCache()
}

export function getAuthToken() {
  return getStorageItem('token')
}

export function getUserId() {
  return getStorageItem('userId')
}

export function getUserName() {
  return getStorageItem('userName')
}

export function getUserNick() {
  return getStorageItem('userNick')
}

export function getDisplayUserName() {
  return getUserNick() || getUserName() || 'Admin'
}

export function getCurrentUserRaw() {
  return getStorageItem('user') || getStorageItem('currentUser') || getStorageItem('userInfo')
}

export function getUserInfo() {
  const raw = getStorageItem('userInfo')
  if (!raw) return {}
  try {
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

export function setAuthSession({
  token,
  userId,
  userName,
  userNick,
  roleCodes,
  userInfo,
  user,
  currentUser,
} = {}) {
  if (token) setStorageItem('token', token)
  if (userId != null) setStorageItem('userId', userId)
  if (userName) setStorageItem('userName', userName)
  if (userNick) setStorageItem('userNick', userNick)
  if (userInfo != null) {
    setStorageItem('userInfo', typeof userInfo === 'string' ? userInfo : JSON.stringify(userInfo))
  }
  if (user != null) {
    setStorageItem('user', typeof user === 'string' ? user : JSON.stringify(user))
  }
  if (currentUser != null) {
    setStorageItem('currentUser', typeof currentUser === 'string' ? currentUser : JSON.stringify(currentUser))
  }
  setUserRoles(roleCodes)
  purgeSessionLoginCache()
}

export function isTokenExpired(token = getAuthToken()) {
  const payload = parseJwtPayload(token)
  if (!payload?.exp) return false
  return Date.now() >= payload.exp * 1000
}

export function clearLoginCache() {
  LOGIN_CACHE_KEYS.forEach((key) => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  })
}

export function isLoginRoute(path) {
  return path === LOGIN_PATH
}

/** 有 token 且未超过 JWT 有效期（管理端当前为 30 天） */
export function isAuthValid() {
  const token = getAuthToken()
  if (!token) return false
  if (isTokenExpired(token)) {
    clearLoginCache()
    return false
  }
  return true
}

export const HOME_PATH = '/admin/home'

const SUPER_ADMIN_ROLE_CODES = ['admin', 'administrator']

export function getUserRoles() {
  const raw = getStorageItem('userRoles')
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean)
    }
  } catch {
    // 兼容旧版逗号分隔
  }
  return raw.split(',').map((item) => item.trim()).filter(Boolean)
}

export function setUserRoles(roleCodes) {
  if (!roleCodes || !roleCodes.length) {
    localStorage.removeItem('userRoles')
    return
  }
  localStorage.setItem('userRoles', JSON.stringify(roleCodes))
}

/** 与后端一致：admin/administrator 角色或 adminId=1 为超管 */
export function isSuperAdmin() {
  const userId = getUserId()
  if (userId === '1' || userId === 1) return true
  const roles = getUserRoles().map((code) => code.toLowerCase())
  return SUPER_ADMIN_ROLE_CODES.some((code) => roles.includes(code))
}

export function getDefaultLandingPath() {
  return isSuperAdmin() ? HOME_PATH : '/admin/product'
}
